/**
 * Avatar — more detailed procedural humanoid.
 *
 * Improvements over v1:
 *  - Better proportions (head/torso/legs ratio closer to real human)
 *  - Face (eyes + mouth drawn on a canvas-texture head sphere)
 *  - Hair visible under the hat
 *  - Separate hands + shoes with distinct colors
 *  - Torso with shirt-and-pants split, belt line
 *  - Jacket/shoulders slightly taller than belly
 *  - Smoother lighting via phong-friendly materials
 *
 * Still instanced-friendly (each avatar is a fresh THREE.Group) but not
 * actually instanced since there's only ever 1 local + small remote count.
 */

import * as THREE from 'three';
import { AnimState } from '@shared/protocol';

const HAT_PALETTE = [
  '#ff5f6d',
  '#ffc371',
  '#7ce0a3',
  '#56c2ff',
  '#bd72ff',
  '#ff8ec0',
  '#f5f0e8',
  '#3a2a44'
];

const SHIRT_PALETTE = [
  '#3d6cb5',
  '#c8383f',
  '#2a8a3d',
  '#b84bb5',
  '#d4a830',
  '#3a3540',
  '#e8e4dc',
  '#4a8fc0'
];

export interface AvatarOptions {
  hatColor?: number;
  shirtColor?: string;
  pantsColor?: string;
  skin?: string;
  hair?: string;
  name?: string;
}

/** Build the head texture once and cache. */
let cachedHeadTexture: THREE.CanvasTexture | null = null;
function getHeadTexture(): THREE.CanvasTexture {
  if (cachedHeadTexture) return cachedHeadTexture;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d')!;
  // Base skin
  ctx.fillStyle = '#dab48a';
  ctx.fillRect(0, 0, 128, 128);
  // Eyes (two white dots with dark pupils) — placed on the "front" of the sphere
  const drawEye = (cx: number) => {
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(cx, 52, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.arc(cx, 53, 3.2, 0, Math.PI * 2);
    ctx.fill();
  };
  drawEye(50);
  drawEye(78);
  // Eyebrows
  ctx.strokeStyle = '#3a2a1a';
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  ctx.moveTo(42, 42); ctx.lineTo(58, 44);
  ctx.moveTo(70, 44); ctx.lineTo(86, 42);
  ctx.stroke();
  // Nose hint
  ctx.strokeStyle = '#b08858';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(64, 58); ctx.lineTo(62, 68); ctx.lineTo(66, 68);
  ctx.stroke();
  // Mouth (closed smile)
  ctx.strokeStyle = '#6a3a2a';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.arc(64, 78, 7, 0.2, Math.PI - 0.2);
  ctx.stroke();
  // Cheek tint
  ctx.fillStyle = 'rgba(240,140,130,0.35)';
  ctx.beginPath(); ctx.arc(46, 66, 5, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(82, 66, 5, 0, Math.PI * 2); ctx.fill();

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  cachedHeadTexture = tex;
  return tex;
}

export class Avatar {
  readonly group: THREE.Group;
  readonly leftArm: THREE.Group;
  readonly rightArm: THREE.Group;
  readonly leftLeg: THREE.Group;
  readonly rightLeg: THREE.Group;
  readonly head: THREE.Group;
  readonly nameplate?: THREE.Sprite;

  private animTime = 0;
  private currentAnim: AnimState = AnimState.Idle;
  private targetAnim: AnimState = AnimState.Idle;

  constructor(group: THREE.Group, parts: {
    leftArm: THREE.Group;
    rightArm: THREE.Group;
    leftLeg: THREE.Group;
    rightLeg: THREE.Group;
    head: THREE.Group;
    nameplate?: THREE.Sprite;
  }) {
    this.group = group;
    this.leftArm = parts.leftArm;
    this.rightArm = parts.rightArm;
    this.leftLeg = parts.leftLeg;
    this.rightLeg = parts.rightLeg;
    this.head = parts.head;
    this.nameplate = parts.nameplate;
  }

  static build(opts: AvatarOptions = {}): Avatar {
    const hatColorIdx = opts.hatColor ?? 0;
    const hatColor = HAT_PALETTE[hatColorIdx % HAT_PALETTE.length];
    const shirt = opts.shirtColor ?? SHIRT_PALETTE[hatColorIdx % SHIRT_PALETTE.length];
    const pants = opts.pantsColor ?? '#2a2a36';
    const skin = opts.skin ?? '#dab48a';
    const hair = opts.hair ?? '#3a2a1a';
    const shoeColor = '#1a1420';

    const root = new THREE.Group();
    root.name = 'Avatar';

    const shirtMat = new THREE.MeshStandardMaterial({ color: shirt, roughness: 0.75 });
    const pantsMat = new THREE.MeshStandardMaterial({ color: pants, roughness: 0.85 });
    const skinMat = new THREE.MeshStandardMaterial({ color: skin, roughness: 0.6 });
    const hatMat = new THREE.MeshStandardMaterial({ color: hatColor, roughness: 0.6 });
    const hairMat = new THREE.MeshStandardMaterial({ color: hair, roughness: 0.7 });
    const shoeMat = new THREE.MeshStandardMaterial({ color: shoeColor, roughness: 0.5 });
    const beltMat = new THREE.MeshStandardMaterial({ color: '#1a120a', roughness: 0.4 });

    /* ── Torso ── */
    // Upper chest (shirt)
    const chest = new THREE.Mesh(
      new THREE.BoxGeometry(0.58, 0.42, 0.32),
      shirtMat
    );
    chest.position.y = 1.28;
    chest.castShadow = true;
    root.add(chest);
    // Belly (slightly narrower)
    const belly = new THREE.Mesh(
      new THREE.BoxGeometry(0.52, 0.28, 0.28),
      shirtMat
    );
    belly.position.y = 0.95;
    belly.castShadow = true;
    root.add(belly);
    // Belt
    const belt = new THREE.Mesh(
      new THREE.BoxGeometry(0.54, 0.06, 0.3),
      beltMat
    );
    belt.position.y = 0.82;
    root.add(belt);
    // Hip / pants top
    const hip = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 0.18, 0.26),
      pantsMat
    );
    hip.position.y = 0.7;
    root.add(hip);
    // Neck
    const neck = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.09, 0.12, 8),
      skinMat
    );
    neck.position.y = 1.56;
    root.add(neck);

    /* ── Head ── */
    const headGroup = new THREE.Group();
    headGroup.position.y = 1.7;
    // Main head — sphere with face texture
    const headMat = new THREE.MeshStandardMaterial({
      map: getHeadTexture(),
      roughness: 0.6
    });
    const headMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.15, 20, 16),
      headMat
    );
    headMesh.castShadow = true;
    // Orient so the face is at +Z (front of avatar)
    headMesh.rotation.y = Math.PI;
    headGroup.add(headMesh);
    // Hair (back of head cap)
    const hairBack = new THREE.Mesh(
      new THREE.SphereGeometry(0.158, 16, 10, 0, Math.PI),
      hairMat
    );
    hairBack.rotation.y = -Math.PI / 2;
    hairBack.position.y = 0.005;
    headGroup.add(hairBack);
    // Hat brim
    const hatBrim = new THREE.Mesh(
      new THREE.CylinderGeometry(0.23, 0.23, 0.02, 16),
      hatMat
    );
    hatBrim.position.y = 0.13;
    headGroup.add(hatBrim);
    // Hat crown
    const hatCrown = new THREE.Mesh(
      new THREE.CylinderGeometry(0.14, 0.16, 0.14, 16),
      hatMat
    );
    hatCrown.position.y = 0.21;
    headGroup.add(hatCrown);

    root.add(headGroup);

    /* ── Arms (pivot at shoulder) ── */
    const buildArm = (side: 1 | -1): THREE.Group => {
      const g = new THREE.Group();
      g.position.set(0.33 * side, 1.45, 0);
      // Upper arm — box so it reads as a sleeve
      const upper = new THREE.Mesh(
        new THREE.BoxGeometry(0.13, 0.34, 0.14),
        shirtMat
      );
      upper.position.y = -0.2;
      upper.castShadow = true;
      g.add(upper);
      // Elbow joint
      const elbow = new THREE.Mesh(
        new THREE.SphereGeometry(0.075, 8, 6),
        shirtMat
      );
      elbow.position.y = -0.38;
      g.add(elbow);
      // Forearm
      const fore = new THREE.Mesh(
        new THREE.BoxGeometry(0.1, 0.26, 0.1),
        skinMat
      );
      fore.position.y = -0.53;
      fore.castShadow = true;
      g.add(fore);
      // Hand
      const hand = new THREE.Mesh(
        new THREE.BoxGeometry(0.09, 0.1, 0.12),
        skinMat
      );
      hand.position.y = -0.7;
      g.add(hand);
      return g;
    };
    const leftArm = buildArm(-1);
    const rightArm = buildArm(1);
    root.add(leftArm, rightArm);

    /* ── Legs (pivot at hip) ── */
    const buildLeg = (side: 1 | -1): THREE.Group => {
      const g = new THREE.Group();
      g.position.set(0.14 * side, 0.68, 0);
      // Thigh
      const thigh = new THREE.Mesh(
        new THREE.BoxGeometry(0.17, 0.38, 0.17),
        pantsMat
      );
      thigh.position.y = -0.22;
      thigh.castShadow = true;
      g.add(thigh);
      // Knee
      const knee = new THREE.Mesh(
        new THREE.SphereGeometry(0.085, 8, 6),
        pantsMat
      );
      knee.position.y = -0.42;
      g.add(knee);
      // Shin
      const shin = new THREE.Mesh(
        new THREE.BoxGeometry(0.14, 0.32, 0.14),
        pantsMat
      );
      shin.position.y = -0.6;
      shin.castShadow = true;
      g.add(shin);
      // Shoe
      const shoe = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.1, 0.28),
        shoeMat
      );
      shoe.position.set(0, -0.8, 0.05);
      g.add(shoe);
      return g;
    };
    const leftLeg = buildLeg(-1);
    const rightLeg = buildLeg(1);
    root.add(leftLeg, rightLeg);

    // Nameplate sprite
    let nameplate: THREE.Sprite | undefined;
    if (opts.name) {
      nameplate = makeNameplate(opts.name);
      nameplate.position.y = 2.1;
      root.add(nameplate);
    }

    return new Avatar(root, {
      leftArm, rightArm, leftLeg, rightLeg,
      head: headGroup, nameplate
    });
  }

  setAnim(state: AnimState): void {
    this.targetAnim = state;
  }

  pose(dt: number, speed: number): void {
    this.animTime += dt;
    if (this.targetAnim !== this.currentAnim) {
      this.currentAnim = this.targetAnim;
    }
    const t = this.animTime;

    this.leftArm.rotation.set(0, 0, 0);
    this.rightArm.rotation.set(0, 0, 0);
    this.leftLeg.rotation.set(0, 0, 0);
    this.rightLeg.rotation.set(0, 0, 0);
    this.head.rotation.set(0, 0, 0);

    switch (this.currentAnim) {
      case AnimState.Walking:
      case AnimState.Running: {
        const cadence = this.currentAnim === AnimState.Running ? 8 : 5;
        const amp = this.currentAnim === AnimState.Running ? 0.7 : 0.5;
        const s = Math.sin(t * cadence) * amp * Math.min(1, speed * 0.5);
        this.leftArm.rotation.x = s;
        this.rightArm.rotation.x = -s;
        this.leftLeg.rotation.x = -s;
        this.rightLeg.rotation.x = s;
        break;
      }
      case AnimState.Dancing: {
        const s = Math.sin(t * 6) * 0.6;
        const c = Math.cos(t * 6) * 0.6;
        this.leftArm.rotation.x = -1.2 + s * 0.3;
        this.rightArm.rotation.x = -1.2 - s * 0.3;
        this.leftArm.rotation.z = 0.4 + s;
        this.rightArm.rotation.z = -0.4 - s;
        this.head.rotation.y = c * 0.4;
        this.group.position.y = Math.abs(Math.sin(t * 4)) * 0.06;
        return;
      }
      case AnimState.Sitting: {
        this.leftLeg.rotation.x = -1.5;
        this.rightLeg.rotation.x = -1.5;
        this.group.position.y -= 0.3;
        return;
      }
      case AnimState.Lying: {
        this.group.rotation.x = -Math.PI / 2;
        return;
      }
      case AnimState.Waving: {
        const s = Math.sin(t * 8) * 0.4;
        this.rightArm.rotation.x = -2.4;
        this.rightArm.rotation.z = -0.6 + s;
        break;
      }
      case AnimState.Headbang: {
        this.head.rotation.x = Math.sin(t * 10) * 0.4 + 0.2;
        const s = Math.sin(t * 10) * 0.3;
        this.leftArm.rotation.x = -1.8;
        this.rightArm.rotation.x = -1.8;
        this.leftArm.rotation.z = 0.3 + s;
        this.rightArm.rotation.z = -0.3 - s;
        break;
      }
      case AnimState.Peace: {
        this.rightArm.rotation.x = -2.0;
        this.rightArm.rotation.z = -0.3;
        break;
      }
      case AnimState.Clap: {
        const s = Math.abs(Math.sin(t * 8)) * 0.5;
        this.leftArm.rotation.x = -1.4;
        this.rightArm.rotation.x = -1.4;
        this.leftArm.rotation.z = 0.3 + s;
        this.rightArm.rotation.z = -0.3 - s;
        break;
      }
      case AnimState.HandsUp: {
        this.leftArm.rotation.x = -2.7;
        this.rightArm.rotation.x = -2.7;
        const s = Math.sin(t * 4) * 0.1;
        this.group.position.y += s * 0.05;
        break;
      }
      case AnimState.Idle:
      default: {
        const s = Math.sin(t * 1.5) * 0.04;
        this.head.rotation.y = s;
        break;
      }
    }
  }

  setName(name: string): void {
    if (this.nameplate) this.group.remove(this.nameplate);
    const np = makeNameplate(name);
    np.position.y = 2.1;
    this.group.add(np);
    (this as { nameplate?: THREE.Sprite }).nameplate = np;
  }

  dispose(): void {
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });
  }
}

function makeNameplate(text: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = 'bold 56px -apple-system, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(10, 6, 18, 0.62)';
  const tw = ctx.measureText(text).width + 64;
  const x = (canvas.width - tw) / 2;
  const y = 24;
  const h = 80;
  const r = 24;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + tw, y, x + tw, y + h, r);
  ctx.arcTo(x + tw, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + tw, y, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#ffc371';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(2.4, 0.6, 1);
  return sprite;
}
